// src/pages/CRM/ActivityComm/MyTasks.js

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Button, Chip, IconButton,
  CircularProgress, Alert, Stack, Fab
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  CheckCircle as CompleteIcon,
  Assignment as TaskIcon,
  Warning as OverdueIcon,
  Today as TodayIcon,
  DateRange as WeekIcon,
  Schedule as UpcomingIcon,
  EventBusy as NoDueDateIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../../utils/permissionUtils';
import { taskService } from './activityCommService';
import {
  TaskStatusLabels, TaskStatusColors,
  TaskPriorityLabels, TaskPriorityColors,
  formatDate
} from './activityCommEnums';
import TaskForm from './TaskForm';

const SECTION_CONFIG = {
  overdue: {
    label: 'Overdue',
    color: 'error.main',
    bgColor: 'error.lighter',
    icon: OverdueIcon
  },
  today: {
    label: 'Due Today',
    color: 'warning.main',
    bgColor: 'warning.lighter',
    icon: TodayIcon
  },
  this_week: {
    label: 'This Week',
    color: 'info.main',
    bgColor: 'info.lighter',
    icon: WeekIcon
  },
  upcoming: {
    label: 'Upcoming',
    color: 'text.primary',
    bgColor: 'grey.100',
    icon: UpcomingIcon
  },
  no_due_date: {
    label: 'No Due Date',
    color: 'text.secondary',
    bgColor: 'grey.50',
    icon: NoDueDateIcon
  }
};

const SECTION_ORDER = ['overdue', 'today', 'this_week', 'upcoming', 'no_due_date'];

const MyTasks = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [groupedTasks, setGroupedTasks] = useState({});
  const [stats, setStats] = useState({ total: 0, overdue: 0, today: 0, completed_this_week: 0 });
  const [createOpen, setCreateOpen] = useState(false);
  const [completingId, setCompletingId] = useState(null);

  useEffect(() => {
    document.title = 'My Tasks | Activity';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      loadMyTasks();
    }
  }, [canView]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
      if (!viewPerm) {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error checking permissions:', err);
      setLoading(false);
    }
  };

  const loadMyTasks = async () => {
    setLoading(true);
    try {
      const response = await taskService.getMyTasks();
      if (response.success) {
        const data = response.data || {};
        setGroupedTasks({
          overdue: data.overdue || [],
          today: data.today || [],
          this_week: data.this_week || [],
          upcoming: data.upcoming || [],
          no_due_date: data.no_due_date || []
        });

        const overdueCount = (data.overdue || []).length;
        const todayCount = (data.today || []).length;
        const allTasks = [
          ...(data.overdue || []),
          ...(data.today || []),
          ...(data.this_week || []),
          ...(data.upcoming || []),
          ...(data.no_due_date || [])
        ];
        const completedThisWeek = allTasks.filter(t => t.status === 'COMPLETED').length;

        setStats({
          total: allTasks.length,
          overdue: overdueCount,
          today: todayCount,
          completed_this_week: data.completed_this_week ?? completedThisWeek
        });
      } else {
        enqueueSnackbar('Failed to load tasks', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to load tasks', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (e, taskId) => {
    e.stopPropagation();
    setCompletingId(taskId);
    try {
      const response = await taskService.complete(taskId);
      if (response.success) {
        enqueueSnackbar('Task completed', { variant: 'success' });
        loadMyTasks();
      }
    } catch (err) {
      enqueueSnackbar('Failed to complete task', { variant: 'error' });
    } finally {
      setCompletingId(null);
    }
  };

  const handleCreateClose = (refresh) => {
    setCreateOpen(false);
    if (refresh) {
      loadMyTasks();
    }
  };

  const totalTasks = stats.total;
  const hasAnyTasks = SECTION_ORDER.some(key => (groupedTasks[key] || []).length > 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, mt: { xs: 2, sm: 10 } }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: { xs: 2, sm: 10 }, mx: 3 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view tasks.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/activity-dashboard`)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            My Tasks
          </Typography>
        </Box>
        {canModify && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            New Task
          </Button>
        )}
      </Box>

      {/* Summary Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <TaskIcon color="primary" sx={{ fontSize: 32, mb: 0.5 }} />
            <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
            <Typography variant="body2" color="text.secondary">Total Tasks</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center', borderTop: 3, borderColor: 'error.main' }}>
            <OverdueIcon color="error" sx={{ fontSize: 32, mb: 0.5 }} />
            <Typography variant="h5" fontWeight={700} color="error.main">{stats.overdue}</Typography>
            <Typography variant="body2" color="text.secondary">Overdue</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center', borderTop: 3, borderColor: 'warning.main' }}>
            <TodayIcon color="warning" sx={{ fontSize: 32, mb: 0.5 }} />
            <Typography variant="h5" fontWeight={700} color="warning.main">{stats.today}</Typography>
            <Typography variant="body2" color="text.secondary">Due Today</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center', borderTop: 3, borderColor: 'success.main' }}>
            <CompleteIcon color="success" sx={{ fontSize: 32, mb: 0.5 }} />
            <Typography variant="h5" fontWeight={700} color="success.main">{stats.completed_this_week}</Typography>
            <Typography variant="body2" color="text.secondary">Completed This Week</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Empty State */}
      {!hasAnyTasks && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <TaskIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No tasks assigned to you
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create a new task or ask your team to assign tasks to you.
          </Typography>
          {canModify && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              New Task
            </Button>
          )}
        </Paper>
      )}

      {/* Grouped Task Sections */}
      {hasAnyTasks && (
        <Stack spacing={3}>
          {SECTION_ORDER.map((sectionKey) => {
            const tasks = groupedTasks[sectionKey] || [];
            if (tasks.length === 0) return null;

            const config = SECTION_CONFIG[sectionKey];
            const SectionIcon = config.icon;

            return (
              <Box key={sectionKey}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <SectionIcon sx={{ color: config.color, fontSize: 20 }} />
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: config.color }}>
                    {config.label}
                  </Typography>
                  <Chip label={tasks.length} size="small" sx={{ ml: 0.5 }} />
                </Box>
                <Stack spacing={1.5}>
                  {tasks.map((task) => (
                    <Paper
                      key={task.id}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        transition: 'box-shadow 0.2s, transform 0.1s',
                        '&:hover': {
                          boxShadow: 4,
                          transform: 'translateY(-1px)'
                        },
                        borderLeft: 4,
                        borderColor: config.color
                      }}
                      onClick={() => navigate(`/chatbot/${chatbotId}/crm/activity/tasks/${task.id}`)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ flex: 1 }}>
                            {task.title}
                          </Typography>
                          <Chip
                            label={TaskPriorityLabels[task.priority] || task.priority}
                            color={TaskPriorityColors[task.priority] || 'default'}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={TaskStatusLabels[task.status] || task.status}
                            color={TaskStatusColors[task.status] || 'default'}
                            size="small"
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {task.due_date && (
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(task.due_date)}
                            </Typography>
                          )}
                          {canModify && task.status !== 'COMPLETED' && (
                            <IconButton
                              size="small"
                              color="success"
                              onClick={(e) => handleComplete(e, task.id)}
                              disabled={completingId === task.id}
                              title="Mark as complete"
                            >
                              <CompleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}

      {/* New Task FAB for mobile */}
      {canModify && hasAnyTasks && (
        <Fab
          color="primary"
          aria-label="new task"
          onClick={() => setCreateOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            display: { xs: 'flex', sm: 'none' }
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Create Task Dialog */}
      <TaskForm
        open={createOpen}
        onClose={handleCreateClose}
        chatbotId={chatbotId}
      />
    </Box>
  );
};

export default MyTasks;
